/**
 * ============================================================
 * useIdiomaAuto
 * ============================================================
 * Composable que detecta automaticamente o idioma do usuário
 * através do IP (cascata de APIs gratuitas) e define o idioma
 * da página APENAS se o usuário ainda não tiver escolhido um
 * manualmente.
 *
 * Uso:
 *   const currentLang = ref('es')
 *   const langManualmenteEscolhido = ref(false)
 *
 *   useIdiomaAuto(currentLang, langManualmenteEscolhido, {
 *     idiomas: ['es', 'pt', 'en'],      // opcional, default: es/pt/en
 *     autoStart: true                   // opcional, default: true
 *   })
 *
 *   const changeLang = (lang) => {
 *     currentLang.value = lang
 *     langManualmenteEscolhido.value = true
 *   }
 *
 * Mapeamento país → idioma:
 *   - Português: BR, PT, AO, MZ, CV, GW, ST, TL
 *   - Inglês   : US, GB, CA, AU, NZ, IE, ZA, SG, IN, PH
 *   - Espanhol : fallback para qualquer outro país
 * ============================================================
 */
import { onMounted } from 'vue'

const PAISES_PT = ['BR', 'PT', 'AO', 'MZ', 'CV', 'GW', 'ST', 'TL']
const PAISES_EN = ['US', 'GB', 'CA', 'AU', 'NZ', 'IE', 'ZA', 'SG', 'IN', 'PH']

const FETCH_TIMEOUT_MS = 3500

/** Timezone IANA → código ISO-2 (fallback quando as APIs de IP falham). */
const TIMEZONE_PARA_PAIS = {
  'America/Sao_Paulo': 'BR',
  'America/Fortaleza': 'BR',
  'America/Recife': 'BR',
  'America/Bahia': 'BR',
  'America/Belem': 'BR',
  'America/Manaus': 'BR',
  'America/Cuiaba': 'BR',
  'America/Campo_Grande': 'BR',
  'America/Porto_Velho': 'BR',
  'America/Boa_Vista': 'BR',
  'America/Rio_Branco': 'BR',
  'America/Noronha': 'BR',
  'America/Santarem': 'BR',
  'America/Maceio': 'BR',
  'America/Araguaina': 'BR',
  'Europe/Lisbon': 'PT',
  'Atlantic/Azores': 'PT',
  'Atlantic/Madeira': 'PT',
  'Europe/Madrid': 'ES',
  'Atlantic/Canary': 'ES',
  'Africa/Ceuta': 'ES',
  'Europe/Malta': 'MT',
  'America/Mexico_City': 'MX',
  'America/Cancun': 'MX',
  'America/Monterrey': 'MX',
  'America/Tijuana': 'MX',
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Argentina/Cordoba': 'AR',
  'America/Argentina/Mendoza': 'AR',
  'America/Santiago': 'CL',
  'America/Bogota': 'CO',
  'America/Lima': 'PE',
  'America/Montevideo': 'UY',
  'America/Asuncion': 'PY',
  'America/La_Paz': 'BO',
  'America/Guayaquil': 'EC',
  'America/Caracas': 'VE',
  'America/Costa_Rica': 'CR',
  'America/Panama': 'PA',
  'America/Guatemala': 'GT',
  'America/El_Salvador': 'SV',
  'America/Tegucigalpa': 'HN',
  'America/Managua': 'NI',
  'America/Santo_Domingo': 'DO',
  'America/Puerto_Rico': 'PR',
  'America/Havana': 'CU',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'Europe/London': 'GB',
  'Europe/Paris': 'FR',
  'Europe/Rome': 'IT',
  'Europe/Berlin': 'DE',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Zurich': 'CH',
  'Europe/Vienna': 'AT',
  'Europe/Dublin': 'IE',
  'Europe/Andorra': 'AD',
  'Africa/Casablanca': 'MA',
  'Africa/Luanda': 'AO'
}

/**
 * Fetch com timeout — evita travar a UI se a API de geo estiver lenta.
 */
const fetchComTimeout = async (url, ms = FETCH_TIMEOUT_MS) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  try {
    return await fetch(url, { cache: 'no-store', signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Fallback 1: timezone do navegador → país.
 */
const codigoPaisPorTimezone = () => {
  try {
    const tz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone
    if (!tz) return null
    if (TIMEZONE_PARA_PAIS[tz]) return TIMEZONE_PARA_PAIS[tz]
    /* Heurística: America/* com nome BR comum (ex: America/Sao_Paulo já coberto). */
    if (tz.startsWith('America/') && /Sao_Paulo|Fortaleza|Recife|Bahia|Manaus|Belem|Cuiaba/i.test(tz)) {
      return 'BR'
    }
    return null
  } catch (_) {
    return null
  }
}

/**
 * Fallback 2: locale do navegador (pt-BR → BR, es-MX → MX, etc.).
 */
const codigoPaisPorIdiomaNavegador = () => {
  try {
    const locales = [
      ...(typeof navigator !== 'undefined' && Array.isArray(navigator.languages)
        ? navigator.languages
        : []),
      typeof navigator !== 'undefined' ? navigator.language : null
    ].filter(Boolean)

    for (const locale of locales) {
      const parts = String(locale).replace('_', '-').split('-')
      if (parts.length >= 2) {
        const region = parts[parts.length - 1].toUpperCase()
        if (/^[A-Z]{2}$/.test(region)) return region
      }
      /* Só idioma, sem região: pt sozinho costuma ser BR no nosso público. */
      if (parts[0]?.toLowerCase() === 'pt') return 'BR'
    }
    return null
  } catch (_) {
    return null
  }
}

/**
 * Detecção síncrona (timezone → locale). Útil para pré-selecionar o DDD
 * imediatamente, sem esperar as APIs de IP.
 */
export const codigoPaisFallbackLocal = () => {
  return codigoPaisPorTimezone() || codigoPaisPorIdiomaNavegador()
}

/**
 * Faz requisições em cascata para determinar o país (ISO-2) a partir do IP.
 * Se todas as APIs falharem, usa timezone e depois locale do navegador.
 * Retorna string em CAIXA ALTA (ex: 'BR') ou null se tudo falhar.
 */
export const buscarCodigoPaisPorIp = async () => {
  try {
    const res = await fetchComTimeout('https://api.country.is/')
    if (res.ok) {
      const data = await res.json()
      if (data?.country) return String(data.country).toUpperCase()
    }
  } catch (_) { /* tenta próxima */ }

  try {
    const res = await fetchComTimeout('https://ipapi.co/json/')
    if (res.ok) {
      const data = await res.json()
      const codigo = data?.country_code || data?.country
      if (codigo) return String(codigo).toUpperCase()
    }
  } catch (_) { /* tenta próxima */ }

  try {
    const res = await fetchComTimeout('https://ipwho.is/')
    if (res.ok) {
      const data = await res.json()
      if (data?.success !== false && data?.country_code) {
        return String(data.country_code).toUpperCase()
      }
    }
  } catch (_) { /* ignora */ }

  /* Fallbacks locais — não dependem de rede (útil com adblock / offline). */
  return codigoPaisFallbackLocal()
}

/**
 * Mapeia código de país ISO → idioma padrão.
 */
export const idiomaPorPais = (codigo) => {
  if (!codigo) return null
  if (PAISES_PT.includes(codigo)) return 'pt'
  if (PAISES_EN.includes(codigo)) return 'en'
  return 'es'
}

/**
 * @param {Ref<string>} currentLang                  — ref reativo do idioma atual
 * @param {Ref<boolean>} langManualmenteEscolhido    — ref que indica se o usuário já escolheu manualmente
 * @param {Object}   [opcoes]
 * @param {string[]} [opcoes.idiomas]                — lista de idiomas suportados na página (default: ['es','pt','en'])
 * @param {boolean}  [opcoes.autoStart]              — inicia automaticamente no onMounted (default: true)
 *
 * @returns {{ detectar: () => Promise<void> }}      — função para disparar a detecção manualmente
 */
export const useIdiomaAuto = (currentLang, langManualmenteEscolhido, opcoes = {}) => {
  const idiomas = opcoes.idiomas || ['es', 'pt', 'en']
  const autoStart = opcoes.autoStart !== false

  const detectar = async () => {
    if (langManualmenteEscolhido?.value) return
    try {
      const codigo = await buscarCodigoPaisPorIp()
      if (!codigo) return
      if (langManualmenteEscolhido?.value) return

      const idioma = idiomaPorPais(codigo)
      if (idioma && idiomas.includes(idioma)) {
        currentLang.value = idioma
      }
    } catch (e) {
      console.warn('[useIdiomaAuto] falha ao detectar idioma por IP:', e)
    }
  }

  if (autoStart) {
    onMounted(detectar)
  }

  return { detectar }
}
